import { useState } from 'react';
import { useFormatter, useTranslations } from 'use-intl';
import Icon from '@/presentation/components/elements/Icon/Icon';
import { Icons } from '@/presentation/components/elements/Icon';
import VaultIcon from '@/presentation/components/elements/VaultIcon';
import CollectionImage from '@/presentation/components/elements/CollectionImage';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { SelectPoolFormFields } from './types';
import { Address } from 'viem';
import { FeeTier, feeTierToPercentage } from 'nftx.js';
import UsingForm from '../UsingForm';

type Form = UseFormReturn<SelectPoolFormFields>;

export interface SelectPoolFormProps {
  symbol: string;
  vaultId?: string;
  collection: Address;
  collectionName: string;
  icon?: string;
  register: Form['register'];
  control: Form['control'];
  errors: Form['formState']['errors'];
  setValue: Form['setValue'];
  isNewVault: boolean; // dont need to show dropdown options as vtoken doesnt exist
}

export const SelectPool = ({
  collection,
  vaultId,
  icon,
  register,
  control,
  errors,
  symbol,
  collectionName,
  isNewVault,
}: SelectPoolFormProps) => {
  const t = useTranslations('CreateLiquidityPositionForms.SelectPool');
  const [showFeeOptions, setShowFeeOptions] = useState(false);
  const { number } = useFormatter();
  const { poolType, feeTier, using } = useWatch({
    control,
  });

  const isPair = poolType === 'pair';
  const isEth = poolType === 'eth';
  const isNft = poolType === 'nft';

  return (
    <div className="space-y-6">
      <fieldset className="">
        <div className="grid grid-cols-3">
          {(['pair', 'eth', 'nft'] as const).map((key, i) => (
            <label
              key={key}
              className={`grid p-px text-center transition-colors ${
                i === 0 ? 'rounded-l-2xl' : i === 2 ? 'rounded-r-2xl' : ''
              } cursor-pointer ${
                key === poolType
                  ? 'bg-gradient-pink-orange'
                  : 'bg-gray-300 hover:bg-gradient-pink-orange-50'
              }`}
            >
              <div
                className={`leading-none p-4 transition-colors ${
                  i === 0
                    ? 'rounded-l-[15px]'
                    : i === 2
                    ? 'rounded-r-[15px]'
                    : ''
                } ${
                  key === poolType
                    ? 'bg-pink-50 dark:bg-black/80'
                    : 'bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  {...register('poolType')}
                  value={key}
                  className="hidden"
                />
                <h4
                  className={`mb-2 font-medium transition-colors ${
                    key === poolType ? 'text-pink-500' : 'text-gray-500'
                  }`}
                >
                  {t(`type.${key}.title`)}
                </h4>
                <span className="text-xs text-gray-500">
                  {t(`type.${key}.text`)}
                </span>
              </div>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <div className="text-xs text-gray-500 font-medium mb-2">
              {'Using'}
            </div>
            {isEth && (
              <div className="flex-1 bg-gray-200 border border-gray-300 rounded-md p-6 flex items-center">
                <Icon
                  name={Icons.ETH}
                  size={24}
                  className="mr-2 text-blue-500"
                />
                <span className="uppercase font-bold text-sm">{'ETH'}</span>
              </div>
            )}
            {isPair && (
              <div className="grid gap-y-1 flex-1">
                <div className="bg-gray-200 border border-gray-300 rounded-md py-1.5 px-4 flex items-center">
                  <Icon
                    name={Icons.ETH}
                    size={20}
                    className="mr-2 text-blue-500"
                  />
                  <span className="uppercase font-bold text-sm">{'ETH'}</span>
                </div>
                {isNewVault ? (
                  <div className="bg-gray-200 border border-gray-300 rounded-md py-1.5 px-4 flex items-center">
                    <CollectionImage
                      collection={collection}
                      className="w-5 h-5 mr-2 flex-none flex"
                    />
                    <span className="uppercase font-bold text-sm">
                      {collectionName}
                    </span>
                  </div>
                ) : (
                  <UsingForm
                    collection={collection}
                    collectionName={collectionName}
                    control={control}
                    size="compact"
                    symbol={symbol}
                    using={using}
                    icon={icon}
                    vaultId={vaultId}
                  />
                )}
              </div>
            )}
            {isNft && isNewVault && (
              <div className="flex-1 bg-gray-200 border border-gray-300 rounded-md p-6 flex items-center">
                <CollectionImage
                  collection={collection}
                  className="w-5 h-5 mr-2 flex-none"
                />
                <span className="uppercase font-bold text-sm">
                  {collectionName}
                </span>
              </div>
            )}
            {isNft && !isNewVault && (
              <UsingForm
                collection={collection}
                collectionName={collectionName}
                control={control}
                size="normal"
                symbol={symbol}
                using={using}
                icon={icon}
                vaultId={vaultId}
              />
            )}
          </div>
          <div className="flex flex-col">
            <div className="text-xs text-gray-500 font-medium mb-2">
              {'Depositing into'}
            </div>
            <div className="flex-1 bg-gray-200 border border-gray-300 rounded-md p-6 flex items-center">
              {vaultId != null ? (
                <VaultIcon vaultId={vaultId} className="w-8 h-8 mr-2" />
              ) : icon ? (
                <img src={icon} alt={symbol} className="w-8 h-8 mr-2" />
              ) : (
                <span className="bg-gray-400 rounded-full w-8 h-8 mr-2 flex-none" />
              )}
              <span className="uppercase font-bold text-sm">{`${symbol}/ETH`}</span>
            </div>
          </div>
        </div>
      </fieldset>
      <fieldset>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <legend className="font-medium flex w-full items-center mb-0.5">
              {t('fee.title')}
              {/* <span className="rounded-full bg-gray-400 h-1 w-1 mx-2" />
              <span className="text-xs text-gray-500">{t('fee.text')}</span> */}
            </legend>
            <p className="text-xs font-medium text-gray-500 mb-1">
              {t('fee.important')}
            </p>
            {!isNewVault && (
              <button
                type="button"
                onClick={() => setShowFeeOptions((x) => !x)}
                className="underline text-pink-500 font-medium text-xs hover:no-underline"
              >
                {t(`fee.advanced.${showFeeOptions ? 'hide' : 'show'}`)}
              </button>
            )}
          </div>
          <div className="col-span-1">
            <div className="bg-gray-200 border border-gray-300 rounded-md p-5 grid place-content-center font-bold">
              {!!feeTier &&
                number(feeTierToPercentage(Number(feeTier)), {
                  style: 'percent',
                  maximumSignificantDigits: 3,
                })}
            </div>
          </div>
        </div>
        {showFeeOptions && (
          <div className="grid grid-cols-3 gap-1 mt-4 text-sm">
            {([3000, 10000] as FeeTier[]).map((key) => {
              const isSelected = key === Number(feeTier);
              return (
                <label
                  key={key}
                  className={`grid p-px text-center transition-colors rounded-xl cursor-pointer ${
                    isSelected
                      ? 'bg-gradient-pink-orange'
                      : 'bg-gray-300 hover:bg-gradient-pink-orange-50'
                  } ${key === 3_000 ? 'font-bold' : ''}`}
                >
                  <div
                    className={`leading-none px-2 py-4 transition-colors rounded-[11px] ${
                      isSelected ? 'bg-pink-50' : 'bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      {...register('feeTier')}
                      value={key}
                      className="hidden"
                    />
                    <h4
                      className={`mb-1.5 font-medium transition-colors ${
                        isSelected ? 'text-pink-500' : 'text-gray-500'
                      }`}
                    >
                      {t(`fee.${key}.title`)}
                    </h4>
                    <span className="text-xs text-gray-500">
                      {t(`fee.${key}.text`)}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </fieldset>
    </div>
  );
};

export default SelectPool;
